import "@/App.css";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/context/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";

// Below-the-fold — lazy chunks
const Bio = lazy(() => import("@/components/Bio").then(m => ({ default: m.Bio })));
const Profile = lazy(() => import("@/components/Profile").then(m => ({ default: m.Profile })));
const Trajectory = lazy(() => import("@/components/Trajectory").then(m => ({ default: m.Trajectory })));
const Achievements = lazy(() => import("@/components/Achievements").then(m => ({ default: m.Achievements })));
const Video = lazy(() => import("@/components/Video").then(m => ({ default: m.Video })));
const Gallery = lazy(() => import("@/components/Gallery").then(m => ({ default: m.Gallery })));
const Contact = lazy(() => import("@/components/Contact").then(m => ({ default: m.Contact })));
const Footer = lazy(() => import("@/components/Footer").then(m => ({ default: m.Footer })));

const Fallback = () => <div style={{ minHeight: "40vh" }} />;

// react-snap (prerender) must see full content for SEO
const IS_SNAP = typeof navigator !== "undefined" && /ReactSnap/.test(navigator.userAgent);

// Renders a below-the-fold section only when it approaches the viewport,
// so its JS chunk, images and embeds don't compete with the hero (LCP).
// Keeps anchor links working: the placeholder carries the section id and
// a hashchange listener forces immediate render when navigated to directly.
const Deferred = ({ id, children }) => {
  const ref = useRef(null);
  const [show, setShow] = useState(IS_SNAP);

  useEffect(() => {
    if (show) return;
    if (!("IntersectionObserver" in window)) {
      setShow(true);
      return;
    }
    const onHash = () => {
      if (id && window.location.hash === `#${id}`) setShow(true);
    };
    onHash();
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "800px 0px" }
    );
    if (ref.current) io.observe(ref.current);
    window.addEventListener("hashchange", onHash);
    return () => {
      io.disconnect();
      window.removeEventListener("hashchange", onHash);
    };
  }, [show, id]);

  // When the section was force-rendered by an anchor click, the browser jumped
  // to the placeholder — re-align to the real section once it is painted.
  useEffect(() => {
    if (show && id && window.location.hash === `#${id}`) {
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView();
      });
    }
  }, [show, id]);

  return (
    <div ref={ref} {...(!show && id ? { id } : {})}>
      {show ? children : <div style={{ minHeight: "35vh" }} />}
    </div>
  );
};

const Home = () => (
  <>
    <Navbar />
    <main>
      <Hero />
      <Suspense fallback={<Fallback />}>
        <Deferred id="bio"><Bio /></Deferred>
        <Deferred id="profile"><Profile /></Deferred>
        <Deferred id="trajectory"><Trajectory /></Deferred>
        <Deferred id="achievements"><Achievements /></Deferred>
        <Deferred id="video"><Video /></Deferred>
        <Deferred id="gallery"><Gallery /></Deferred>
        <Deferred id="contact"><Contact /></Deferred>
      </Suspense>
    </main>
    <Suspense fallback={null}>
      <Deferred><Footer /></Deferred>
    </Suspense>
  </>
);

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
          </Routes>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
